import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import getChartColorsArray from "Components/Common/ChartsDynamicColor";
import { getPaymentsRegionGenderDistribution } from "common/services/payments.service";
import useQueryState from "Components/Hooks/UseQueryState";
import moment from "moment";
import { FilterType, defaultFilter } from "../Filters";

const convertToSeries = (response: any[]) => {
  // Step 1: Extract all unique individual categories
  const categories = Array.from(
    new Set(response.flatMap((item) => Object.keys(item.individuals)))
  );

  // Step 2: Map each category to its data values for each region
  const countSeries = categories.map((category) => {
    return {
      name: category,
      data: response.map((item) => item.individuals[category]?.count || 0), // Default to 0 if the category is missing
    };
  });

  const amountPerMonthSeries = categories.map((category) => {
    return {
      name: category,
      data: response.map(
        (item) => item.individuals[category]?.totalAmountPerMonth || 0
      ), // Default to 0 if the category is missing
    };
  });

  return { countSeries, amountPerMonthSeries };
};

const AmountDistributionChart = ({
  categories,
  series,
}: {
  categories: string[];
  series: { name: string; data: number[] }[];
}) => {
  var chartColumnStackedColors = getChartColorsArray(
    '["#1C92D8", "#F06C6B", "#CCCCCC"]'
  );

  const options: any = {
    chart: {
      stacked: !0,
      stackType: "100%",
      toolbar: {
        show: !1,
      },
    },
    plotOptions: {
      bar: {
        horizontal: !0,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    title: {
      text: "",
      style: {
        fontWeight: 600,
      },
    },
    xaxis: {
      categories,
    },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return "$ " + val.toLocaleString();
        },
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      offsetX: 40,
    },
    colors: chartColumnStackedColors,
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      options={options}
      series={series}
      type="bar"
      height={350}
    />
  );
};

const ContractDistributionChart = ({
  categories,
  series,
}: {
  categories: string[];
  series: { name: string; data: number[] }[];
}) => {
  const chartColumnStackedColors = getChartColorsArray(
    '["#1C92D8", "#F06C6B", "#CCCCCC"]'
  );

  const options: any = {
    chart: {
      stacked: !0,
      stackType: "100%",
      toolbar: {
        show: !1,
      },
    },
    plotOptions: {
      bar: {
        horizontal: !0,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    title: {
      text: "",
      style: {
        fontWeight: 600,
      },
    },
    xaxis: {
      categories,
    },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return val.toLocaleString() + " Contracts";
        },
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      offsetX: 40,
    },
    colors: chartColumnStackedColors,
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      options={options}
      series={series}
      type="bar"
      height={350}
    />
  );
};

const RegionGenderDistribution = () => {
  const [filter] = useQueryState<FilterType>("filter", defaultFilter);

  const [payments, setPayments] = useState([]);
  const [, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsRegionGenderDistribution({
          mdaId: filter?.mda?.value || undefined,
          regionId: filter?.region?.value || undefined,
          positionTypeId: filter?.positionType?.value || undefined,
          paymentCategoryId: filter?.paymentCategory?.value || undefined,
          thematicAreaId: filter?.thematicArea?.value || undefined,
          sectorId: filter?.sector?.value || undefined,
          agencyId: filter?.agency?.value || undefined,
          startDate: filter?.dateRange?.fromDate
            ? moment(filter.dateRange.fromDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
          endDate: filter?.dateRange?.toDate
            ? moment(filter.dateRange.toDate)
                .toDate()
                .toLocaleDateString("en-CA")
            : undefined,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filter?.agency?.value,
    filter?.dateRange?.fromDate,
    filter?.dateRange?.toDate,
    filter.mda,
    filter?.positionType?.value,
    filter?.region?.value,
    filter?.paymentCategory?.value,
    filter?.sector?.value,
    filter?.thematicArea?.value,
  ]);

  const [activeTab, setActiveTab] = useState("number");

  const { countSeries, amountPerMonthSeries } = React.useMemo(
    () => convertToSeries(payments),
    [payments]
  );
  const categories = useMemo(() => {
    return payments.map((payment: any) => payment.region);
  }, [payments]);

  return (
    <Card>
      <CardHeader className="d-flex align-items-center ">
        <div className="flex-grow-1">
          <h4 className="card-title mb-0">
            Contract Distribution by Region and Gender
          </h4>
        </div>
        <div className="d-flex gap-1">
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "number" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("number")}
          >
            NUMBER
          </button>
          <button
            type="button"
            className={`btn btn-sm material-shadow-none ${
              activeTab === "amount" ? "btn-soft-primary" : "btn-soft-secondary"
            }`}
            onClick={() => setActiveTab("amount")}
          >
            AMOUNT
          </button>
        </div>
      </CardHeader>
      <CardBody>
        {activeTab === "amount" ? (
          <AmountDistributionChart
            categories={categories}
            series={amountPerMonthSeries}
          />
        ) : (
          <ContractDistributionChart
            categories={categories}
            series={countSeries}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default RegionGenderDistribution;
